import { Select, Typography } from '@automata/ui';
import { logger } from '@automata/utils';
import { OrgScriptsInfo } from '@automata/wfb-api';
import MenuItem from '@mui/material/MenuItem';
import { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import { startCase } from 'lodash';
import { useCallback } from 'react';

import { selectScripts } from '../../features/workflow/selectors';
import { useAppSelector } from '../../hooks';

interface ScriptSelectProps {
  label: string;
  value: string | null;
  onChange: (value: string | null) => void;
}

const NO_SCRIPT_SELECTED = 'NO_SCRIPT_SELECTED';

export const ScriptSelect = ({ label, value, onChange }: ScriptSelectProps) => {
  const scripts = useAppSelector<OrgScriptsInfo>(selectScripts);

  const scriptDescription = scripts.functions.find(
    (x) => x.function_name === value
  )?.description;

  const handleChange = useCallback(
    (e: SelectChangeEvent<unknown>) => {
      const value = e.target.value;
      if (value === null || typeof value === 'string') {
        onChange(value);
      } else {
        logger.error('ContentField: unexpected value type for:', value);
      }
    },
    [onChange]
  );

  return (
    <>
      <Select
        label={label}
        value={value === null ? NO_SCRIPT_SELECTED : value}
        onChange={handleChange}
      >
        {/* Special menu item for case where it's null */}
        {value === null ? (
          <MenuItem value={NO_SCRIPT_SELECTED} disabled>
            <Typography fontStyle="italic">No script selected</Typography>
          </MenuItem>
        ) : null}
        {scripts.functions.map((script) => (
          <MenuItem key={script.function_name} value={script.function_name}>
            <Stack
              direction="row"
              gap="0.25rem"
              justifyContent="space-between"
              width="100%"
            >
              <Stack>
                <Typography variant="body1">
                  {startCase(script.function_name)}
                </Typography>
              </Stack>
            </Stack>
          </MenuItem>
        ))}
      </Select>
      {scriptDescription && (
        <Stack
          gap={1}
          sx={{ backgroundColor: 'grey.100', p: 1, borderRadius: 1 }}
        >
          <Typography variant="overline">Details</Typography>
          <Typography>{scriptDescription}</Typography>
        </Stack>
      )}
    </>
  );
};
